import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { format, isBefore } from 'date-fns';
import * as Popover from '@radix-ui/react-popover';
import { Button, Divider } from '@features/ui';
import { ArrowRightIcon, ChevronDownIcon } from '@features/ui/icons';
import {
  allDaysInMonth,
  cn,
  dateIsAfter,
  dateIsSameOrAfter,
  dateIsValid,
  DateType,
  dateUpdateMonth,
  dateUpdateYear,
  DateValueType,
  endDayOfMonth,
  firstDayOfMonth,
  formatDate,
  getNextDates,
  nextDaysInWeek,
  nextMonthBy,
  previousDaysInWeek,
  previousMonthBy,
  weekDayStringToIndex,
} from '@lib/utils.ts';
import { Days, Months, Years } from '@features/ui/components/calendars';
import RoundedButton from '@features/ui/components/calendars/RoundedButton.tsx';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
} from 'lucide-react';
import { START_WEEK } from '@src/constants';
import { cva, type VariantProps } from 'class-variance-authority';

const NUMBER_YEARS_SHOW = 12;
const CALENDAR_SIZE = 42;

const pickerVariants = cva('flex items-center justify-between', {
  variants: {
    variant: {
      default: 'px-5 hover:bg-accent rounded-lg bg-[#2F3C54] gap-x-5',
      outline:
        'px-5 hover:bg-accent gap-x-5 border-1 border-[#C6C6C6] hover:bg-accent',
    },
    size: {
      default: 'h-11 min-w-[295px] px-5 rounded-lg gap-x-2 text-sm font-bold',
      xs: 'h-6.5 rounded-[3px] px-1 min-w-64 gap-x-1',
      sm: 'h-9.5 rounded-[3px] px-3 min-w-[239px] gap-x-2 text-xs font-normal',
      lg: 'h-11 rounded-[5px] min-w-[242px] px-6 min-w-64 gap-x-1',
    },
  },
  defaultVariants: {
    size: 'default',
    variant: 'default',
  },
});

interface DateRangePickerProps {
  startFrom?: DateType;
  onChange: (
    value: DateValueType,
    e?: HTMLInputElement | null | undefined
  ) => void;
  minDate?: DateType;
  maxDate?: DateType;
  value: DateValueType;
  asSingle?: boolean;
  hideHeader?: boolean;
  rightIcon?: ReactNode;
  className?: string;
  placeholder?: string;
  color?: string;
}

export const DateRangePicker: FC<
  DateRangePickerProps & VariantProps<typeof pickerVariants>
> = ({
  minDate,
  onChange,
  maxDate,
  startFrom = null,
  value = null,
  asSingle = false,
  hideHeader,
  variant,
  size,
  rightIcon,
  className,
  placeholder = 'Select Date',
  color,
}) => {
  const [open, setOpen] = useState(false);
  const [showYears, setShowYears] = useState(false);
  const [showMonths, setShowMonths] = useState(false);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [firstDate, setFirstDate] = useState<Date>(
    startFrom && dateIsValid(startFrom) ? startFrom : new Date()
  );
  const [secondDate, setSecondDate] = useState<Date>(nextMonthBy(firstDate));

  const firstGotoDate = useCallback(
    (date: Date) => {
      if (dateIsSameOrAfter(date, secondDate, 'date')) {
        setSecondDate(nextMonthBy(date));
      }
      setFirstDate(date);
    },
    [secondDate]
  );

  const handleDateClick = useCallback(
    (day: Date) => {
      if (asSingle) {
        onChange({ startDate: day, endDate: null });
        setOpen(false);
        setHoverDate(null);

        return;
      }

      if (!value?.startDate || (value?.startDate && value?.endDate)) {
        onChange({ startDate: day, endDate: null });
      } else if (value?.startDate && !value?.endDate) {
        if (isBefore(day, value?.startDate)) {
          onChange({ startDate: day, endDate: value?.startDate });
        } else {
          onChange({ startDate: value?.startDate, endDate: day });
        }
        setOpen(false);
      }
      setHoverDate(null);
    },
    [asSingle, onChange, value?.endDate, value?.startDate]
  );

  const previousMonthFirst = useCallback(
    (day?: Date) => {
      if (day) {
        handleDateClick(day);
      }
      setFirstDate(previousMonthBy(firstDate));
    },
    [firstDate, handleDateClick]
  );

  const nextMonthFirst = useCallback(
    (day?: Date) => {
      firstGotoDate(nextMonthBy(firstDate));
      if (day) {
        handleDateClick(day);
      }
    },
    [firstDate, firstGotoDate, handleDateClick]
  );

  const changeFirstMonth = useCallback(
    (month: number) => {
      firstGotoDate(dateUpdateMonth(firstDate, month - 1));
    },
    [firstDate, firstGotoDate]
  );

  const changeFirstYear = useCallback(
    (year: number) => {
      firstGotoDate(dateUpdateYear(firstDate, year));
    },
    [firstDate, firstGotoDate]
  );

  const calendarData = useMemo(() => {
    const firstDateCurrentMonth = firstDayOfMonth(firstDate);
    const lastDateCurrentMonth = endDayOfMonth(firstDate);

    const startWeekOnIndex = weekDayStringToIndex(START_WEEK);

    const previous = previousDaysInWeek(
      firstDateCurrentMonth,
      startWeekOnIndex
    );
    const current = allDaysInMonth(firstDate);
    const next = nextDaysInWeek(lastDateCurrentMonth, startWeekOnIndex);

    const remainingDaysLength =
      CALENDAR_SIZE - (previous.length + current.length + next.length);

    if (remainingDaysLength > 0) {
      const lastNextDate = next[next.length - 1] || current[current.length - 1];

      next.push(...getNextDates(lastNextDate, remainingDaysLength));
    }

    return {
      previous: previous,
      current: current,
      next: next,
    };
  }, [firstDate]);

  useEffect(() => {
    if (startFrom && dateIsValid(startFrom)) {
      const startDate = value?.startDate;
      const endDate = value?.endDate;

      if (startDate && dateIsValid(startDate)) {
        setFirstDate(startDate);
        if (!asSingle) {
          if (
            endDate &&
            dateIsValid(endDate) &&
            dateIsAfter(firstDayOfMonth(endDate), startDate, 'date')
          ) {
            setSecondDate(endDate);
          } else {
            setSecondDate(nextMonthBy(startDate));
          }
        }
      } else {
        setFirstDate(startFrom);
        setSecondDate(nextMonthBy(startFrom));
      }
    }
  }, [asSingle, startFrom, value]);

  const hideMonths = useCallback(() => {
    if (showMonths) setShowMonths(false);
  }, [showMonths]);

  const hideYears = useCallback(() => {
    if (showYears) setShowYears(false);
  }, [showYears]);

  const clickYear = useCallback(
    (year: number) => {
      setTimeout(() => {
        onChange(null);
        changeFirstYear(year);
        setShowYears(!showYears);
      }, 250);
    },
    [changeFirstYear, onChange, showYears]
  );

  const years = useMemo(() => {
    return {
      min: minDate && dateIsValid(minDate) ? minDate.getFullYear() : null,
      max: maxDate && dateIsValid(maxDate) ? maxDate.getFullYear() : null,
    };
  }, [maxDate, minDate]);

  const clickMonth = useCallback(
    (month: number) => {
      setTimeout(() => {
        onChange(null);
        changeFirstMonth(month);
        setShowMonths(!showMonths);
      }, 250);
    },
    [changeFirstMonth, onChange, showMonths]
  );

  return (
    <div className="relative text-left">
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <div
            className={cn(
              'cursor-pointer',
              pickerVariants({ variant, size, className })
            )}
            style={{ borderColor: color }}
          >
            <span
              className={cn(
                'text-xs',
                value?.startDate ? 'text-white' : 'text-muted-foreground'
              )}
              style={{ color }}
            >
              {value?.startDate
                ? asSingle
                  ? formatDate(value?.startDate.toISOString())
                  : `${formatDate(value?.startDate.toISOString())} - ${value?.endDate ? formatDate(value?.endDate.toISOString()) : '...'}`
                : placeholder}
            </span>
            {rightIcon ? rightIcon : <ChevronDownIcon fill={color} />}
          </div>
        </Popover.Trigger>

        <Popover.Content
          align="start"
          className="!bg-[#262626] flex flex-col z-10 mt-2 bg-neutral-800 text-white rounded-xl shadow-lg w-auto"
          collisionPadding={16}
          side="bottom"
          sideOffset={8}
          onMouseLeave={() => setHoverDate(null)}
        >
          {!hideHeader && (
            <div className={'p-6 flex gap-x-4 items-center'}>
              <p>Start Date</p>
              <ArrowRightIcon />
              <p>End Date</p>
            </div>
          )}
          <Divider />
          <div className="flex items-center space-x-1.5 border border-[#CECECE] rounded-md px-2 py-1.5 m-4">
            {!showMonths && !showYears && (
              <div className="flex-none">
                <RoundedButton
                  roundedFull={true}
                  onClick={() => previousMonthFirst()}
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </RoundedButton>
              </div>
            )}

            {showYears && (
              <div className="flex-none">
                <RoundedButton
                  roundedFull={true}
                  onClick={() => {
                    changeFirstYear(
                      firstDate.getFullYear() - NUMBER_YEARS_SHOW
                    );
                  }}
                >
                  <ChevronsLeftIcon className="h-5 w-5" />
                </RoundedButton>
              </div>
            )}

            <div className="flex flex-1 items-center space-x-1.5">
              <div className="w-1/2">
                <RoundedButton
                  onClick={() => {
                    setShowMonths(!showMonths);
                    hideYears();
                  }}
                >
                  {format(firstDate, 'MMMM')}
                </RoundedButton>
              </div>

              <div className="w-1/2">
                <RoundedButton
                  onClick={() => {
                    setShowYears(!showYears);
                    hideMonths();
                  }}
                >
                  <>{firstDate.getFullYear()}</>
                </RoundedButton>
              </div>
            </div>
            {showYears && (
              <div className="flex-none">
                <RoundedButton
                  roundedFull={true}
                  onClick={() => {
                    changeFirstYear(
                      firstDate.getFullYear() + NUMBER_YEARS_SHOW
                    );
                  }}
                >
                  <ChevronsRightIcon className="h-5 w-5" />
                </RoundedButton>
              </div>
            )}

            {!showMonths && !showYears && (
              <div className="flex-none">
                <RoundedButton
                  roundedFull={true}
                  onClick={() => nextMonthFirst()}
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </RoundedButton>
              </div>
            )}
          </div>

          {showMonths && (
            <Months
              clickMonth={clickMonth}
              currentMonth={firstDate.getMonth() + 1}
            />
          )}

          {showYears && (
            <Years
              clickYear={clickYear}
              currentYear={firstDate.getFullYear()}
              maxYear={years.max}
              minYear={years.min}
              year={firstDate.getFullYear()}
            />
          )}

          {!showMonths && !showYears && (
            <>
              <Days
                asSingle={asSingle}
                calendarData={calendarData}
                endDate={value?.endDate ?? null}
                hoverDate={hoverDate}
                setHoverDate={setHoverDate}
                startDate={value?.startDate ?? null}
                onClickNextDays={nextMonthFirst}
                onClickPreviousDays={previousMonthFirst}
                onDateClick={handleDateClick}
              />
            </>
          )}

          <Divider />
          <div className="p-6">
            <Button
              className={'h-9 rounded-full'}
              variant={'outline'}
              onClick={() => {
                onChange(null);
                setHoverDate(null);
              }}
            >
              Reset
            </Button>
          </div>
        </Popover.Content>
      </Popover.Root>
    </div>
  );
};
